import { TextField } from '@blueprinthq/joy'
import {
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Radio,
  RadioGroup,
  VStack
} from '@chakra-ui/react'
import {
  Field,
  type FieldProps,
  Form,
  Formik,
  type FormikProps,
  useFormikContext
} from 'formik'
import { DateTime } from 'luxon'
import React from 'react'
import InputMask from 'react-input-mask'
import * as Yup from 'yup'
import { useReminderControllerSetReminder } from '~/clinician-api'
import type { SetReminderDto } from '~/clinician-api/models'
import type { ContactMethod } from '.'

export const ClinicianSessionReminderFieldsSchema = Yup.object().shape({
  date: Yup.string()
    .required('Please enter a date')
    .default(DateTime.now().toFormat('yyyy-MM-dd')),
  timeOfDay: Yup.string()
    .required('Please enter a time')
    .default(DateTime.now().toFormat('HH:mm')),
  contactMethod: Yup.string().required('Required').default('email'),
  email: Yup.string().when('contactMethod', {
    is: (value: string) => value === 'email' || value === 'both',
    then: y => y.email('Invalid email address').required('Required'),
    otherwise: y => y.nullable()
  }),
  phone: Yup.string().when('contactMethod', {
    is: (value: string) => value === 'sms' || value === 'both',
    then: schema =>
      schema
        .matches(
          /^\(\d{3}\) \d{3}-\d{4}$/,
          'Phone number must be in format (XXX) XXX-XXXX'
        )
        .required('Required'),
    otherwise: schema => schema.nullable()
  })
})

interface ClinicianSessionReminderFormProps {
  user: any
  setShowSuccess: (showSuccess: boolean) => void
  reminder: SetReminderDto | undefined
  children: (props: any) => React.ReactNode
}

export const ClinicianSessionReminderForm = ({
  user,
  setShowSuccess,
  reminder,
  children
}: ClinicianSessionReminderFormProps) => {
  const { mutateAsync: setReminder } = useReminderControllerSetReminder({
    mutation: {
      onSuccess: () => {
        setShowSuccess(true)
      }
    }
  })

  const dateTime = reminder?.nextAppointmentDateTime
    ? DateTime.fromISO(reminder.nextAppointmentDateTime)
    : DateTime.now()
  const contactMethod = (reminder?.contactMethod as ContactMethod) ?? 'email'
  const email = reminder?.email ?? user.email
  const phone = reminder?.phoneNumber ?? ''

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        date: dateTime.toFormat('yyyy-MM-dd'),
        timeOfDay: dateTime.toFormat('HH:mm'),
        contactMethod: contactMethod,
        email: email,
        phone: phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
      }}
      validationSchema={ClinicianSessionReminderFieldsSchema}
      onSubmit={async values => {
        const dateTime = DateTime.fromFormat(
          `${values.date} ${values.timeOfDay}`,
          'yyyy-MM-dd HH:mm'
        )

        await setReminder({
          data: {
            nextAppointmentDateTime: dateTime.toISO()!,
            email: values.email,
            phoneNumber: values.phone,
            contactMethod: values.contactMethod
          }
        })
      }}
    >
      {(formikProps: FormikProps<any>) => (
        <Form>{children({ formikProps })}</Form>
      )}
    </Formik>
  )
}

export const ClinicianSessionReminderFields = ({ user }: { user: any }) => {
  const { values } = useFormikContext<any>()

  return (
    <Box>
      <VStack
        spacing="small"
        justifyContent="flex-start"
        alignItems="flex-start"
      >
        <Field pb="xsmall" name="date">
          {({ field, meta }: FieldProps) => (
            <FormControl isInvalid={!!meta.error}>
              <FormLabel fontWeight="bold">Session date:</FormLabel>
              <Input w="100%" {...field} type="date" />
              <FormErrorMessage>{meta.error}</FormErrorMessage>
            </FormControl>
          )}
        </Field>
        <Field pb="xsmall" name="timeOfDay">
          {({ field, meta }: FieldProps) => (
            <FormControl isInvalid={!!meta.error}>
              <FormLabel fontWeight="bold">Session start time:</FormLabel>
              <Input w="100%" {...field} type="time" />
              <FormErrorMessage>{meta.error}</FormErrorMessage>
            </FormControl>
          )}
        </Field>
        <Field pb="xsmall" name="contactMethod">
          {({ field, form, meta }: FieldProps) => (
            <FormControl isInvalid={!!meta.error}>
              <FormLabel fontWeight="bold">Delivery method:</FormLabel>
              <RadioGroup
                value={field.value}
                onChange={(nextValue: string) => {
                  form.setFieldValue(field.name, nextValue)
                }}
              >
                <HStack spacing={5}>
                  <Radio _focus={{ outline: 'none' }} value="email">
                    Email
                  </Radio>
                  <Radio _focus={{ outline: 'none' }} value="sms">
                    Text Message
                  </Radio>
                  <Radio _focus={{ outline: 'none' }} value="both">
                    Both
                  </Radio>
                </HStack>
              </RadioGroup>
              <FormErrorMessage>{meta.error}</FormErrorMessage>
            </FormControl>
          )}
        </Field>
        {values.contactMethod !== 'sms' && (
          <Field pb="xsmall" name="email">
            {({ field, meta }: FieldProps) => (
              <FormControl isInvalid={!!meta.error}>
                <FormLabel fontWeight="bold" htmlFor="email">
                  Email:
                </FormLabel>
                <TextField {...field} width="100%" label="" id="email" />
                <FormErrorMessage>{meta.error}</FormErrorMessage>
              </FormControl>
            )}
          </Field>
        )}
        {values.contactMethod !== 'email' && (
          <Field name="phone">
            {({ field, meta }: FieldProps) => {
              return (
                <Box width="100%" mb="16px">
                  <FormControl isInvalid={meta.touched && !!meta.error}>
                    <FormLabel fontWeight="bold" htmlFor="phone">
                      Mobile number:
                    </FormLabel>
                    <InputMask
                      mask="(999) 999-9999"
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                    >
                      {(inputProps: any) => (
                        <Input
                          {...inputProps}
                          {...field}
                          id="phone"
                          type="tel"
                          height="48px"
                          placeholder="(123) 456-7890"
                        />
                      )}
                    </InputMask>
                    <FormErrorMessage>{meta.error}</FormErrorMessage>
                  </FormControl>
                </Box>
              )
            }}
          </Field>
        )}
      </VStack>
    </Box>
  )
}
